import React, { FC, useEffect, useState } from 'react';

import { Button } from '@mui/material';

import styles from './ContextManagementView.module.scss';
import { PowerplantContextModel } from '../PowerplantContextModel';
import ContextModelDiagram from './ContextModelDiagram/ContextModelDiagram';
import ContextModelTagInfo from './ContextModelTagInfo/ContextModelTagInfo';
import ContextModelSensorMapping from './ContextModelSensorMapping/ContextModelSensorMapping';
import ContextModelWordMapping from 'pages/ContextManagerPage/ContextManagementView/ContextModelWordMapping/ContextModelWordMapping';
import { WordMappingModel } from './WordMappingModel';
import { ContextManagerService } from 'pages/ContextManagerPage/ContextManagerService';
import AlertDialog from 'dialogs/AlertDialog/AlertDialog';
import { AlertConfigModel } from 'dialogs/AlertDialog/AlertConfigModel';

interface ContextManagementViewProps {
  powerplantModel: PowerplantContextModel;
  powerplantName: string;
  onRecreate: (powerplantName: string) => void;
}

export const ContextManagementView: FC<ContextManagementViewProps> = (
  {
    powerplantModel,
    powerplantName,
    onRecreate
  }
) => {

  const [currentAlert, setCurrentAlert] = useState<AlertConfigModel | null>(null);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [wordMappings, setWordMappings] = useState<WordMappingModel[]>([]);

  useEffect(() => {
    getWordMappings();
  }, []);

  const findItemByCode = (data: any, itemId: string): any | undefined => {
    // Base case: if the current item's code matches the search query
    if (data.code === itemId) {
      return data;
    }

    // If the item has children, iterate over each child and search recursively
    if (data.children) {
      for (const childKey in data.children) {
        if (data.children.hasOwnProperty(childKey)) {
          const result = findItemByCode(data.children[childKey], itemId);
          // If the item is found in the subtree, return it
          if (result) {
            return result;
          }
        }
      }
    }

    // If the item is not found in the current item or its children, return undefined
    return undefined;
  }

  const getWordMappings = async (): Promise<void> => {
    setIsFetching(true);
    try {
      const fetchedWordMappings: WordMappingModel[] = await ContextManagerService.getWordMappings();

      if (!fetchedWordMappings) {
        throw new Error('Error getting word mappings')
      }

      setWordMappings(fetchedWordMappings);
    } catch (error) {
      setWordMappings([]);
      console.error('Failed getting word mappings');
      const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed getting word mappings. Please refresh, and contact support if the issue persists.'};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    } finally {
      setIsFetching(false);
    }
  }

  const selectNodeHandler = (nodeData: any): void => {
    let item;
    for (const systemKey in powerplantModel.systems) {
      item = findItemByCode(powerplantModel.systems[systemKey], nodeData.id);
      if (item) {
        break;
      }
    }

    setSelectedNode(item);
  };

  const unselectAllHandler = (): void => {
    setSelectedNode(null);
  };

  return (
    <div
      className={styles.ContextManagementView}
      data-testid="ContextManagementView"
    >
      <section className={styles.rebuildButtonWrapper}>
        <Button
          className={styles.rebuildButton}
          variant={'contained'}
          onClick={() => onRecreate(powerplantName)}
        >
          Rebuild Model
        </Button>
      </section>

      <section className={styles.contextManagementGrid}>
        <ContextModelDiagram powerplantModel={powerplantModel} powerplantName={powerplantName} onUnselectAll={unselectAllHandler} onSelectNode={selectNodeHandler} />
        <ContextModelTagInfo selectedNode={selectedNode} />
        <ContextModelSensorMapping powerplantName={powerplantName} wordMappings={wordMappings} />
        <ContextModelWordMapping isParentFetching={isFetching} onUpdate={getWordMappings} wordMappings={wordMappings} />
      </section>

      { isAlertDialogOpen && currentAlert &&
        <AlertDialog
          onClose={() => {
            setIsAlertDialogOpen(false);
            setCurrentAlert(null);
          }}
          title={currentAlert.title}
          message={currentAlert.message}
        ></AlertDialog>
      }
    </div>
  );
}

export default ContextManagementView;
